/**
 * 地区数据池：已启用国家的地区树 + 扁平映射与父级链，带缓存
 * 用于 RegionDisplay、RegionSelect 等组件的统一数据源
 */
import { getDisplayTextByLocale } from '@/utils/locale';
import { getLocationTree, listLocationCountries } from './location';

/** 默认使用柬埔寨（无已启用国家时的回退） */
const DEFAULT_COUNTRY_CODE = 'KH';

/** 地区节点 + 从根到当前节点的父级链（不含自身） */
export interface LocationNodeWithParents {
  node: API.LocationTreeNode;
  parentChain: API.LocationTreeNode[];
}

/** 数据池返回：树 + code -> 节点与父级链（业务侧统一用 code 存储与匹配） */
export interface EnabledCountryLocationData {
  countryCode: string;
  countryName: string;
  tree: API.LocationTreeNode[];
  /** code -> 节点与从根到其父级的链，用于展示层级路径 */
  mapByCode: Map<string, LocationNodeWithParents>;
}

/** 动态文案：按当前选中的语言优先，否则回退 zh_CN -> en_US -> km_KH -> name_en */
export function getLocationDisplayName(node: API.LocationTreeNode): string {
  const text = getDisplayTextByLocale(node?.display_text as Record<string, string> | undefined, '-');
  if (text !== '-') return text;
  return node?.name_en ?? '-';
}

/** 从根到当前节点的完整路径（含自身）的显示名称数组 */
export function getLocationPathDisplayNames(
  parentChain: API.LocationTreeNode[],
  node: API.LocationTreeNode,
): string[] {
  return [...parentChain.map(getLocationDisplayName), getLocationDisplayName(node)];
}

// --- 缓存 ---
let cachedCountryCode: string | null = null;
let cachedData: EnabledCountryLocationData | null = null;
let loadPromise: Promise<EnabledCountryLocationData> | null = null;

function buildFlatMapByCode(
  tree: API.LocationTreeNode[],
  parentChain: API.LocationTreeNode[] = [],
): Map<string, LocationNodeWithParents> {
  const map = new Map<string, LocationNodeWithParents>();
  const walk = (nodes: API.LocationTreeNode[], parents: API.LocationTreeNode[]) => {
    for (const node of nodes) {
      map.set(node.code, { node, parentChain: [...parents] });
      if (node.children?.length) {
        walk(node.children, [...parents, node]);
      }
    }
  };
  walk(tree, []);
  return map;
}

/**
 * 获取已启用国家的地区数据（树 + 扁平映射与父级链）
 * 先查已启用国家，无则使用默认国家代码（柬埔寨）；带内存缓存，避免重复请求
 */
export async function getEnabledCountryLocationData(): Promise<EnabledCountryLocationData> {
  // 若已有缓存且未失效，直接返回（缓存不按 country 失效，仅内存常驻，刷新页即清）
  if (cachedData && cachedCountryCode) {
    return cachedData;
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async (): Promise<EnabledCountryLocationData> => {
    const countriesRes = await listLocationCountries();
    let countryCode = DEFAULT_COUNTRY_CODE;
    let countryName = '';

    if (countriesRes.code === 0 && countriesRes.data?.length) {
      const enabled = countriesRes.data.find((c) => c.is_active === true);
      if (enabled) {
        countryCode = enabled.country_code;
        countryName = enabled.country_name ?? '';
      }
    }

    const treeRes = await getLocationTree(countryCode);
    const tree: API.LocationTreeNode[] =
      treeRes.code === 0 && treeRes.data?.tree?.length ? treeRes.data.tree : [];
    const mapByCode = buildFlatMapByCode(tree);

    const data: EnabledCountryLocationData = {
      countryCode,
      countryName: countryName || treeRes.data?.country_name || countryCode,
      tree,
      mapByCode,
    };

    cachedCountryCode = countryCode;
    cachedData = data;
    return data;
  })();

  try {
    return await loadPromise;
  } finally {
    loadPromise = null;
  }
}

/**
 * 清除缓存（例如地区管理页启用/同步后希望列表刷新时可选调用）
 */
export function clearEnabledCountryLocationCache(): void {
  cachedCountryCode = null;
  cachedData = null;
  loadPromise = null;
}
